<template>
  <div class="ics_wrapper">
    <base-overlay :show="loading">
      <base-sub-card
        buttonToggle
        showControls
        :sideControl="false"
        :ConfidenceOptions="false"
        :showToggle="false"
        v-if="Object.keys(values).length"
        @dropdownTypeSelected="
          downLoadType($event, {
            indicator: values.indicator.short_name,
            datasource: values.datasource.datasource,
            year: '',
          })
        "
      >
        <template #title>
          <!-- Display comparison text when the selected indicator is not an array -->
          <div class="d-flex align-items-center justify-content-between w-100">
            <p
              class="work-sans mb-0 line-height"
              v-if="!Array.isArray(values.indicator.length)"
            >
            Comparison of selected indicators across {{ values.compareBy.name}}s <b>({{ values.datasource.datasource }})</b>
            </p>
            <button
              class="relationship-btn"
              @click="showRelationshipPopup = true"
              title="Show indicator relationships"
              type="button"
              aria-haspopup="dialog"
            >
              Show indicator relationships
            </button>
          </div>
          <!-- <p class="text-dark work-sans mb-0 line-height" v-else>
           Comparison of <b>{{ values.indicator[0].short_name }}</b> and
            <b>{{ values.indicator[1].short_name }}</b>
            according to the
            <b> {{ values.datasource.datasource }} </b> across
            {{ values.compareBy.name }}s
          </p> -->
        </template>
        <BarChart ref="BaseChart"
        :title="title"
        :categoryLabel="'Indicators'"
        :chartOptions="chartOptions" />
      </base-sub-card>
    </base-overlay>
    <!-- Display 'no_data' block when data is not loading and checkData() returns false -->
    <!-- <div v-if="!loading && !checkData() " class="no_data">
      <img
        :src="require('@/assets/no-data/No_Available_Data.svg')"
        alt="no data"
        class="img-fluid"
        height="auto"
        width="240px"
      />
    </div> -->
    <!-- Display the "no_indicator_data" block only when the data is not loading and there are indicators without available data -->
    <div v-if="!loading && filteredIndicators.length > 0" class="no_ind_data">
      <ul>
        <li v-for="indicator in filteredIndicators" :key="indicator.id">
          <b>{{ indicator.short_name }}</b>: {{ getIndicatorNoDataMessage(indicator) }}
        </li>
      </ul>
    </div>
  <!-- Indicator Relationship Popup -->
  <IndicatorRelationshipPopup
    :show="showRelationshipPopup"
    :indicators="getIndicatorsWithRelations"
    @close="showRelationshipPopup = false"
  />
  </div>

</template>

<script>
import { mapMutations, mapActions } from 'vuex';
import moment from 'moment';
import AdvancedControlPanelSetup from '@/modules/msdat-dashboard/mixins/advanced-control-panel-setup';
import BarChart from '@/components/Barchart/BaseBarChart.vue';
import defaultOptions from '@/components/Barchart/defaultOption';
import chartDownload from '../mixins/chart_download';
import IndicatorRelationshipPopup from './IndicatorRelationshipPopup.vue';

export default {
  name: 'ICS',
  mixins: [chartDownload, AdvancedControlPanelSetup],
  components: {
    BarChart,
    IndicatorRelationshipPopup,
  },
  data() {
    return {
      title: '',
      color: ['#17606B', '#E85D58'],
      dataSeries: [],
      loading: false,
      chartOptions: {},
      indicatorNoDataReasons: {},
      showRelationshipPopup: false,
      chartRequestId: 0,
      lastChartKey: '',
    };
  },

  props: {
    values: {
      type: [Object, String, Array],
      required: true,
    },
    controlIndex: {
      type: Number,
      required: true,
    },
  },

  // methods: {
  //   formatHighChartData(dataSeries) {
  //     this.chartOptions = {
  //       chart: {
  //         type: 'line',
  //       },
  //       series: [],
  //     };
  //     this.chartOptions.series.push(dataSeries);
  //   },
  // },
  computed: {
    filteredIndicators() {
    // Check if this.values.indicator is an array before using filter
      if (Array.isArray(this.values.indicator)) {
        return this.values.indicator.filter((indicator) => !this.hasDataForIndicator(indicator));
      }
      if (this.values.indicator && !this.hasDataForIndicator(this.values.indicator)) {
        return [this.values.indicator];
      }
      return [];
    },
    getIndicatorsWithRelations() {
      const indicators = Array.isArray(this.values.indicator)
        ? this.values.indicator
        : [this.values.indicator];

      // Return selected indicators for analysis (not related indicators)
      return indicators.map((indicator) => ({
        ...indicator,
        selectedIndicators: indicators, // Pass all selected indicators for analysis
      }));
    },
  },
  methods: {
    ...mapActions('MSDAT_STORE', ['SET_CONTROL_OPTIONS']),
    ...mapMutations('MSDAT_STORE', [
      'TOGGLE_VISIBILITY', 'SETUP_CONTROL_OPTIONS1',
    ]),
    hasDataForIndicator(indicator) {
      // Check if chartOptions and its series are defined
      if (this.chartOptions?.series) {
        // Exclude "Skilled birth attendance" from the check it is considered to have data
        if (indicator.short_name.toLowerCase() === 'skilled birth attendance') {
          return true;
        }
        // Find the series corresponding to the indicator
        const indicatorSeries = this.chartOptions.series.find(
          (series) => series.name.toLowerCase().includes(indicator.full_name.toLowerCase()),
        );

        // Check if the indicatorSeries has data points
        return indicatorSeries?.data && indicatorSeries.data.length > 0;
      }
      // Return false if chartOptions or series are not defined
      return false;
    },
    getDatasourceLabel() {
      return this.values.datasource?.datasource
        || this.values.datasource?.['full_name']
        || this.values.datasource?.name
        || 'the selected data source';
    },
    getComparisonContextLabel() {
      const compareBy = this.values.compareBy?.name;

      if (compareBy === 'Period') {
        return this.values.location?.name || 'the selected location';
      }

      if (compareBy === 'State') {
        return this.values.year || 'the selected year';
      }

      if (compareBy === 'National') {
        return 'national comparison';
      }

      if (compareBy === 'Zonal') {
        return 'zonal comparison';
      }

      return 'the selected comparison';
    },
    getIndicatorSelectionLabel(indicator) {
      if (indicator) {
        return indicator.short_name || indicator.full_name || 'the selected indicator';
      }

      const indicators = Array.isArray(this.values.indicator)
        ? this.values.indicator.filter(Boolean)
        : [this.values.indicator].filter(Boolean);

      if (indicators.length === 1) {
        return indicators[0].short_name || indicators[0].full_name || 'the selected indicator';
      }

      if (indicators.length > 1) {
        return `${indicators.slice(0, 2).map((item) => item.short_name || item.full_name).join(' and ')} indicators`;
      }

      return 'the selected indicator(s)';
    },
    datasourceMatches(selectedDatasource, availableDatasource) {
      if (!selectedDatasource || !availableDatasource) return false;

      const selectedId = Number(selectedDatasource.id);
      const availableId = Number(availableDatasource.id);
      if (Number.isFinite(selectedId) && Number.isFinite(availableId) && selectedId === availableId) {
        return true;
      }

      const selectedNames = [
        selectedDatasource.datasource,
        selectedDatasource.full_name,
        selectedDatasource.name,
      ]
        .map((name) => String(name || '').trim().toLowerCase())
        .filter(Boolean);

      const availableNames = [
        availableDatasource.datasource,
        availableDatasource.full_name,
        availableDatasource.name,
      ]
        .map((name) => String(name || '').trim().toLowerCase())
        .filter(Boolean);

      return selectedNames.some((name) => availableNames.includes(name));
    },
    async getIndicatorNoDataReason(indicator) {
      const datasourceLabel = this.getDatasourceLabel();
      const comparisonLabel = this.getComparisonContextLabel();
      const indicatorLabel = this.getIndicatorSelectionLabel(indicator);

      try {
        const availableDatasources = await this.getDataSourcesFromIndicator(indicator.id);
        const datasourceAvailable = availableDatasources.some((availableDatasource) => this.datasourceMatches(this.values.datasource, availableDatasource));

        if (!datasourceAvailable) {
          return `${datasourceLabel} does not have data for ${indicatorLabel}.`;
        }

        return `No data is available for ${indicator.short_name} in ${datasourceLabel} for ${comparisonLabel}.`;
      } catch (error) {
        console.error('Failed to resolve indicator availability:', error);
        return `${datasourceLabel} does not have data for ${indicatorLabel}.`;
      }
    },
    async syncNoDataReasons() {
      const indicators = Array.isArray(this.values.indicator)
        ? this.values.indicator.filter(Boolean)
        : [this.values.indicator].filter(Boolean);

      if (!indicators.length) {
        this.indicatorNoDataReasons = {};
        return;
      }

      const resolvedReasons = await Promise.all(indicators.map(async (indicator) => [
        indicator.id,
        await this.getIndicatorNoDataReason(indicator),
      ]));

      this.indicatorNoDataReasons = resolvedReasons.reduce((accumulator, [indicatorId, reason]) => ({
        ...accumulator,
        [indicatorId]: reason,
      }), {});
    },
    getIndicatorNoDataMessage(indicator) {
      return this.indicatorNoDataReasons[indicator.id]
        || `No data is available for ${indicator.short_name}.`;
    },
    checkData() {
      const datar = this.chartOptions?.series?.map((el, i) => el.data[i]);
      if (datar !== undefined) {
        if (datar[0]?.length >= 1) {
          return true;
        }
        return false;
      }
      return false;
    },
    displayFactorSign(factor) {
      const dpfactor = factor;
      let sign;
      switch (dpfactor) {
        case 'in percentage':
          sign = '%';
          break;
        default:
          sign = '';
      }
      return sign;
    },
    async plotForState(values) {
      const highChartOptions = {
        chart: {
          type: 'column',
        },
        series: [],
        yAxis: [],
        xAxis: {
          type: 'category',
          min: 0,
          labels: {
            x: 30,
          },
        },
        plotOptions: {
          series: {
            grouping: true,
            pointWidth: 10,
            connectNulls: false,
            pointPlacement: 'between',
            // borderWidth: 0,
          },
        },
      };
      let indicators = '';
      if (!Array.isArray(values.indicator)) {
        indicators = [values.indicator];
      } else {
        indicators = values.indicator;
      }
      const dataPromises = indicators?.map((item) => this.dlQuery({
        indicator: item.id,
        datasource: values.datasource.id,
        period: values.year,
        location: {
          level: 3,
        },
      }));

      const results = await Promise.all(dataPromises);
      /**
       * Map the display factors for the different indicators
       */
      const yTitles = [];
      for (let i = 0; i < results.length; i += 1) {
        const indicator = indicators[i];
        const displayFactor = this.dlGetFactor(indicator.factor) || { display_factor: '' };
        yTitles.push(displayFactor.display_factor);
      }

      for (let i = 0; i < results.length; i += 1) {
        // formate result to HighChart Format
        const indicator = indicators[i];
        const data = results[i];
        const toHighChartFormat = data?.map((item) => [
          this.dlGetLocation(item.location).name,
          parseFloat(item.value),
        ]);
        const displayFactor = this.dlGetFactor(indicator.factor) || { display_factor: '' };
        const yAxis = {
          yAxis: [
            {
              plotLines: [],
              labels: {
                style: {
                  fontFamily: 'Work Sans, sans-serif',
                  fontSize: '11px',
                },
              },
              title: {
                style: {
                  ...defaultOptions.yAxis.title.style,
                  fontSize: '10px',
                },
              },
            },
          ],
          title: {
            ...defaultOptions.yAxis.title,
            // text: displayFactor.display_factor,
            text: [...new Set(yTitles)].join(' | '),
          },
          opposite: !!i, // this will become either true of false as 0 or 1
        };

        const obj = {
          color: this.color[i],
          dataLabels: {
            enabled: true,
            format: `{y}${this.displayFactorSign(
              displayFactor.display_factor,
            )}`,
            style: {
              ...defaultOptions.yAxis.title.style,
              fontSize: '10px',
            },
          },
          // name: indicator.full_name,
          name: `${indicator.full_name} ${displayFactor.display_factor.trim() ? `(${displayFactor.display_factor})` : ''}`,
          data: toHighChartFormat,
        };
        // highChartOptions.yAxis.push(yAxis);
        if (i === 0) highChartOptions.yAxis.push(yAxis);
        if (toHighChartFormat.length) highChartOptions.series.push(obj);
      }
      return highChartOptions;
    },
    /**
     * This method loops through the list of indicators
     * and creates the SDG target line object if the
     * target exists and finally adds it to the chart
     */
    toggleSDGTargetLine(val) {
      let indicators = null;
      const plotLines = [];
      const options = { ...this.chartOptions };
      if (val) {
        if (!Array.isArray(this.values.indicator)) {
          indicators = [this.values.indicator];
        } else {
          indicators = this.values.indicator;
        }
        for (let n = 0; n < indicators.length; n++) {
          if (indicators[n].sdg_target) {
            plotLines.push({
              width: 0.5,
              id: 'sdgTarget',
              color: '#222222',
              value: indicators[n].sdg_target,
              dashStyle: 'longdashdot',
              label: {
                text: 'SDG',
                verticalAlign: 'top',
                rotation: 0,
                textAlign: 'right',
                y: 0,
                x: 0,
                style: {
                  fontSize: '10px',
                  fontFamily: '"Open Sans", sans-serif',
                },
              },
            });
            // Checks if the plotLines object exists
            if (options.yAxis[0]?.plotLines) {
              options.yAxis[0].plotLines.push(...plotLines);
            } else {
              options.yAxis[0].plotLines = [];
              options.yAxis[0].plotLines.push(...plotLines);
            }
            this.chartOptions = { ...options };
          }
        }
      } else {
        // Checks if the plotLines object exists
        if (options.yAxis[0]?.plotLines) {
          const temp = options.yAxis[0].plotLines.filter((line) => line.id !== 'sdgTarget');
          options.yAxis[0].plotLines = temp;
        } else {
          options.yAxis[0].plotLines = [];
          const temp = options.yAxis[0].plotLines.filter((line) => line.id !== 'sdgTarget');
          options.yAxis[0].plotLines = temp;
        }
        this.chartOptions = { ...options };
      }
    },
    /**
     * This method loops through the list of indicators
     * and creates the national target line object if the
     * target exists and finally adds it to the chart
     */
    toggleNationalTargetLine(val) {
      let indicators = null;
      const plotLines = [];
      const options = { ...this.chartOptions };
      if (val) {
        if (!Array.isArray(this.values.indicator)) {
          indicators = [this.values.indicator];
        } else {
          indicators = this.values.indicator;
        }
        for (let n = 0; n < indicators.length; n++) {
          if (indicators[n].national_target) {
            plotLines.push({
              width: 0.5,
              color: '#222222',
              id: 'ntLine',
              value: indicators[n].national_target,
              label: {
                text: 'NT',
                verticalAlign: 'top',
                rotation: 0,
                textAlign: 'left',
                y: 0,
                x: -13,
                style: {
                  fontSize: '10px',
                  fontFamily: '"Open Sans", sans-serif',
                },
              },
            });
            // Checks if the plotLines object exists
            if (options.yAxis[0]?.plotLines) {
              options.yAxis[0].plotLines.push(...plotLines);
            } else {
              options.yAxis[0].plotLines = [];
              options.yAxis[0].plotLines.push(...plotLines);
            }
            this.chartOptions = { ...options };
          }
        }
      } else {
        // Checks if the plotLines object exists
        if (options.yAxis[0]?.plotLines) {
          const temp = options.yAxis[0].plotLines.filter((line) => line.id !== 'ntLine');
          options.yAxis[0].plotLines = temp;
        } else {
          options.yAxis[0].plotLines = [];
          const temp = options.yAxis[0].plotLines.filter((line) => line.id !== 'ntLine');
          options.yAxis[0].plotLines = temp;
        }

        this.chartOptions = { ...options };
      }
    },
    getTargetPlotLines(showSdg, showNational) {
      const indicators = Array.isArray(this.values.indicator)
        ? this.values.indicator
        : [this.values.indicator];
      const plotLines = [];

      indicators.forEach((indicator) => {
        const sdgTarget = indicator?.['sdg_target'];
        const nationalTarget = indicator?.['national_target'];

        if (showSdg && sdgTarget) {
          plotLines.push({
            width: 0.5,
            id: 'sdgTarget',
            color: '#222222',
            value: sdgTarget,
            dashStyle: 'longdashdot',
            label: {
              text: 'SDG',
              verticalAlign: 'top',
              rotation: 0,
              textAlign: 'right',
              y: 0,
              x: 0,
              style: {
                fontSize: '10px',
                fontFamily: '"Open Sans", sans-serif',
              },
            },
          });
        }

        if (showNational && nationalTarget) {
          plotLines.push({
            width: 0.5,
            color: '#222222',
            id: 'ntLine',
            value: nationalTarget,
            label: {
              text: 'NT',
              verticalAlign: 'top',
              rotation: 0,
              textAlign: 'left',
              y: 0,
              x: -13,
              style: {
                fontSize: '10px',
                fontFamily: '"Open Sans", sans-serif',
              },
            },
          });
        }
      });

      return plotLines;
    },
    async plotForPeriods(values) {
      const highChartOptions = {
        chart: {
          type: 'line',
        },
        yAxis: [],
        series: [],
      };
      let indicators = '';
      if (!Array.isArray(values.indicator)) {
        indicators = [values.indicator];
      } else {
        indicators = values.indicator;
      }

      const dataPromises = indicators?.map((item) => this.dlQuery({
        indicator: item.id,
        datasource: values.datasource.id,
        location: values.location.id,
      }));

      const results = await Promise.all(dataPromises);
      // debugger;
      /**
       * Map the display factors for the different indicators
       */
      const yTitles = [];
      for (let i = 0; i < results.length; i += 1) {
        const indicator = indicators[i];
        const displayFactor = this.dlGetFactor(indicator.factor) || { display_factor: '' };
        yTitles.push(displayFactor.display_factor);
      }

      for (let i = 0; i < results.length; i += 1) {
        const result = results[i];
        const indicator = indicators[i];
        const filterOnlyYearlyValues = result.filter((item) => moment(item.period, 'YYYY', true).isValid());
        const formatToHighChartFormat = filterOnlyYearlyValues?.map((item) => [
          item.period,
          Number.parseFloat(item.value),
        ]);
        const sortTheYear = formatToHighChartFormat.sort((a, b) => a[0] - b[0]);

        const displayFactor = this.dlGetFactor(indicator.factor) || { display_factor: '' };
        if (i === 0) {
          highChartOptions.yAxis.push({
            yAxis: [
              {
                plotLines: [],
                labels: {
                  style: {
                    fontFamily: 'Work Sans, sans-serif',
                    fontSize: '11px',
                  },
                },
                title: {
                  style: {
                    ...defaultOptions.yAxis.title.style,
                    fontSize: '10px',
                  },
                },
              },
            ],
            title: {
              ...defaultOptions.yAxis.title,
              // text: displayFactor.display_factor,
              text: [...new Set(yTitles)].join(' | '),
            },
            opposite: !!i, // this will become either true of false as 0 or 1
          });
        }
        const obj = {
          color: this.color[i],
          lineWidth: 3,
          // name: indicator.full_name,
          name: `${indicator.full_name} ${displayFactor.display_factor.trim() ? `(${displayFactor.display_factor})` : ''}`,
          data: sortTheYear,
        };
        if (sortTheYear.length) highChartOptions.series.push(obj);
      }
      // this functions checks to make years apear from smallest to highest when the first selected indicator
      // year have higher values than that of the second selected indicator
      if (highChartOptions.series.length > 1 && highChartOptions.series[0].data[0] > highChartOptions.series[1].data[0]) {
        const temporary = highChartOptions.series[1];
        highChartOptions.series[1] = highChartOptions.series[0];
        highChartOptions.series[0] = temporary;
      }
      return highChartOptions;
    },

    async renderChart(panelValues, requestId = this.chartRequestId) {
      const { sdg, national } = this.values.target;
      let highChartOptions = null;

      if (panelValues.compareBy.name === 'Period') {
        highChartOptions = await this.plotForPeriods(panelValues);
      }

      if (panelValues.compareBy.name === 'State') {
        highChartOptions = await this.plotForState(panelValues);
      }

      if (highChartOptions?.yAxis?.[0]) {
        const existingPlotLines = highChartOptions.yAxis[0].plotLines || [];
        highChartOptions.yAxis[0].plotLines = [
          ...existingPlotLines.filter((line) => line.id !== 'sdgTarget' && line.id !== 'ntLine'),
          ...this.getTargetPlotLines(sdg, national),
        ];
      }

      if (requestId === this.chartRequestId) {
        this.chartOptions = highChartOptions ? { ...highChartOptions } : {};
        await this.syncNoDataReasons();
      }
    },
    getChartKey(values) {
      const indicatorIds = Array.isArray(values.indicator)
        ? values.indicator.map((indicator) => indicator?.id).join(',')
        : values.indicator?.id;

      return [
        indicatorIds,
        values.datasource?.id,
        values.year,
        values.location?.id,
        values.compareBy?.name,
        values.target?.sdg,
        values.target?.national,
      ].join('|');
    },
    async refreshChart() {
      if (!this.values.indicator || !this.values.datasource?.id || !this.values.compareBy?.name) {
        return;
      }

      const chartKey = this.getChartKey(this.values);
      if (chartKey === this.lastChartKey && Object.keys(this.chartOptions).length) return;
      this.lastChartKey = chartKey;

      const requestId = this.chartRequestId + 1;
      this.chartRequestId = requestId;
      this.loading = true;

      try {
        await this.renderChart(this.values, requestId);
      } finally {
        if (requestId === this.chartRequestId) {
          this.loading = false;
        }
      }
    },
  },
  watch: {
    'values.indicator': {
      async handler() {
        await this.refreshChart();
      },
      immediate: true,
      // deep: true
    },
    'values.datasource': {
      async handler() {
        await this.refreshChart();
      },
      // immediate: true,
      // deep: true,
    },
    'values.year': {
      async handler() {
        await this.refreshChart();
      },
    },
    'values.location': {
      async handler() {
        await this.refreshChart();
      },
    },
    'values.compareBy': {
      async handler(data) {
        if (data.name === 'Period') {
          this.TOGGLE_VISIBILITY({
            panelIndex: this.controlIndex,
            key: 'year',
            value: false,
          });

          this.TOGGLE_VISIBILITY({
            panelIndex: this.controlIndex,
            key: 'location',
            value: true,
          });
          await this.refreshChart();
        } else if (data.name === 'State') {
          this.TOGGLE_VISIBILITY({
            panelIndex: this.controlIndex,
            key: 'year',
            value: true,
          });
          this.TOGGLE_VISIBILITY({
            panelIndex: this.controlIndex,
            key: 'location',
            value: false,
          });

          // get the list of years by datasource
          const setYearDropdown = await this.setYearDropdown();
          // period dropdown;
          this.SET_CONTROL_OPTIONS({
            panelIndex: 2,
            controlIndex: 2,
            values: setYearDropdown,
          });
          await this.refreshChart();
        }
      },
      immediate: false,
    },
    /**
     * This adds or removes the target line
     * without re-plotting the entire chart
     */
    'values.target.sdg': {
      handler(val) {
        this.toggleSDGTargetLine(val);
      },
    },
    /**
     * This adds or removes the target line
     * without re-plotting the entire chart
     */
    'values.target.national': {
      handler(val) {
        this.toggleNationalTargetLine(val);
      },
    },
  },

  async mounted() {
    if (!this.values?.datasource) {
      this.$store.commit('MSDAT_STORE/SET_PAYLOAD', {
        controlIndex: 2,
        key: 'datasource',
        value: this.dlDatasource.find((dat) => dat.id === 8),
      });
    }
    if (!Array.isArray(this.values.indicator.length)) {
      this.title = ` Comparison Of ${this.values.indicator.short_name} according to the ${this.values.datasource.datasource} across ${this.values.compareBy.name}`;
    } else {
      this.title = ` Comparison Of ${this.values.indicator[0].short_name} and ${this.values.indicator[1].short_name} according to the ${this.values.datasource.datasource} across ${this.values.compareBy.name}s`;
    }
  },
};
</script>

<style lang="scss" scoped>
div.ics_wrapper {
  position: relative;
  div.no_data {
    position: absolute;
    top: 0;

    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }
}

.no_ind_data {
  position:fixed ;
  top:280px;
  right: 110px;
  padding: 10px;
  background-color: #ffffff;
  z-index: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 4px;

  ul{
    list-style: none;
    padding: 0;
    margin: 0;
    position: sticky;
  }

  li {
    font-size: 12px;
    font-weight: 400;
    color: #000000;
    line-height: 1.5;
    letter-spacing: 0.5px;
    text-align: left;
    margin-bottom: 0.5rem;
  }
}

.relationship-btn {
  background: transparent;
  color: #17606B;
  cursor: pointer;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #E85D58;
    text-decoration: underline;
    background-color: rgba(23, 96, 107, 0.06);
  }
}

.report-icon {
  font-size: 17px;
  color: #17606B;
}
</style>
